// Tariff/TariffOrder.tsx
import React, {useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {ITariff} from '../../Types/psychology/tariff';
import {Message} from 'Core/components/Message';
import CircularProgress from 'Core/components/elements/CircularProgress';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import {axios} from 'Auth/axiosConfig';
import {ICurrencyWithPrice, IPaymentSystem} from '../../Types/commerce/shop';
import PaymentSelector from "Order/PaymentSelector";
import {Typography} from '@mui/material';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import pprint from "../../Utils/pprint";
import {ClientRequest} from "../ConsultationPackage/PackageOrder";
import {checkClientRequest} from "Consultation/clientRequest";
import {FC, FCC, FR} from "WideLayout/Layouts";
import UserAvatar from "User/UserAvatar";
import StarRating from "Core/components/StarRating";
import {useSelector} from "react-redux";
import {RootState} from "Redux/store";

const TariffOrder: React.FC = () => {
    const {isAuthenticated} = useContext(AuthContext) as AuthContextType;
    const {byResponse} = useErrorProcessing();
    const {skill_categories, status} = useSelector((state: RootState) => state.psychologyParams);
    const {productId} = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const [product, setProduct] = useState<ITariff | null>(location.state?.product || null);
    const [loading, setLoading] = useState<boolean>(!location.state?.product && !!productId);
    const [error, setError] = useState<string | null>(null);
    const [orderCreating, setOrderCreating] = useState<boolean>(false);
    const [pickedData, setPickedData] = useState<ClientRequest | null>(null);
    const [hasConsultationPackage, setHasConsultationPackage] = useState<boolean>(false);

    const isConsultationsPackageInProduct = () => {
        if (!product || !product.products) return false;
        return product.products.some((p) => p.polymorphic_ctype && p.polymorphic_ctype.model === 'consultationspackage');
    }

    useEffect(() => {
        if (isAuthenticated !== null) {
            if (!isAuthenticated) {
                navigate('/auth', {state: {from: location}, replace: true});
                Message.info('Мы сохранили ваш выбор, войдите или зарегистрируйтесь для продолжения.', 1, 9000);
                return;
            }
        }
        if (!product) return;
        const hasPackage = isConsultationsPackageInProduct();
        setHasConsultationPackage(hasPackage);
        if (hasPackage) {
            const clientRequest = checkClientRequest(false, navigate, location);
            if (clientRequest) {
                setPickedData(clientRequest);
            }
            // TODO: Если выбранный психолог не консультирует по конфигурациям в тарифе то нужно
            //  перекинуть на фильтр психологов с понятным сообщением при чем с указанием нужных конфигов, сейчас
            //  страница с психологами умеет принимать только 1 конфиг ?config=N. Очевидно
            //  должна быть кнопка Сменить психолога на всех типах заказа что он есть(Тариф и пакет по идее).
        }
        setLoading(false);
    }, [isAuthenticated, navigate, location, product]);

    useEffect(() => {
        if (!product && productId) {
            const fetchProduct = async () => {
                setLoading(true);
                try {
                    const response = await axios.get<ITariff>(`/api/v1/tariffs/${productId}/`);
                    pprint('Tariff for Order');
                    pprint(response.data);
                    setProduct(response.data);
                } catch (err: any) {
                    console.error('Ошибка при загрузке продукта:', err);
                    setError('Не удалось загрузить продукт.');
                } finally {
                    setLoading(false);
                }
            };
            fetchProduct();
        }
    }, [product, productId]);

    const createOrder = async (
        currency: ICurrencyWithPrice,
        paymentSystem: IPaymentSystem,
        promocode: number | null,
        email?: string
    ) => {
        if (!product) {
            Message.error('Продукт не найден.');
            return;
        }
        if (!currency) {
            Message.error('Выберите валюту.');
            return;
        }
        if (!paymentSystem) {
            Message.error('Выберите способ оплаты.');
            return;
        }
        setOrderCreating(true);
        try {
            let payload: any = {
                product: product.id,
                currency: currency.currency,
                payment_system: paymentSystem,
                promocode: promocode ? promocode : null,
            };

            if (email) {
                payload.email = email;
            }

            if (hasConsultationPackage && pickedData) {
                if (!pickedData.selected_slot) {
                    Message.error('Время, тип или длительность для консультации не были выбраны.', 0, 7000);
                    setOrderCreating(false);
                    return;
                }

                payload = {
                    product: product.id,
                    psychologist: pickedData?.psychologist.user.id,
                    psychologist_skills: pickedData?.skills,
                    //date_first_consultation: pickedData?.selected_slot.format(),
                    currency: currency.currency,
                    payment_system: paymentSystem,
                    consultation_config: pickedData?.consultation_config.id,
                    has_children: pickedData?.is_psychologist_has_child
                        ? pickedData.is_psychologist_has_child === 'yes'
                            ? true
                            : pickedData.is_psychologist_has_child === 'no'
                                ? false
                                : null
                        : null,
                    mental_health: pickedData?.mental_health,
                    client_gender: pickedData?.client_gender,
                    //reservation_paid: pickedData?.reservation_paid,
                    promocode: promocode ? promocode : null,
                };

                if (email) payload.email = email;

                const response = await axios.post('/api/v1/orders/create/', payload);
                Message.success('Заказ успешно создан.');
                window.open(response.data, '_blank');
                navigate(`/orders`);
                return;
            }

            const response = await axios.post('/api/v1/orders/tariff/', payload);
            Message.success('Заказ успешно создан.');
            window.location.href = response.data;
        } catch (error) {
            byResponse(error);
            setOrderCreating(false);
        }
    };

    if (loading) {
        return (
            <CircularProgress size={'150px'}/>
        );
    }

    if (error) {
        return (
            <Typography color="error" align="center" mt={4}>
                {error}
            </Typography>
        );
    }

    if (!product) {
        return (
            <Typography align="center" mt={4}>
                Продукт не найден.
            </Typography>
        );
    }

    return (
        <div className={'fc gap-2'} style={{maxWidth: '600px', margin: '0 auto', padding: '1rem'}}>
            <h2>{product.name}</h2>
            <p style={{whiteSpace: 'pre-wrap'}}>{product.description}</p>
            {isConsultationsPackageInProduct() && <FC g={1}>
                <FC g={1} maxW={340}>
                    <FR g={2}>
                        <FC h={'min-content'} pos={'relative'}>
                            <UserAvatar
                                size={'7rem'}
                                roundedClassName={'rounded-3'}
                                avatar={pickedData?.psychologist?.user.avatar}
                            />
                            <StarRating
                                rating={pickedData?.psychologist?.rating}
                                starClassName={'fs-6'}
                                ratingWrapperClassName={'position-absolute z-5 bottom-0 left-0 w-100'}
                                ratingContentClassName={`frcc px-2 w-100 backdrop-blur-10 rounded-2`}
                                ratingValueClassName={`d-none`}
                            />
                        </FC>
                        <FCC g={1} grow>
                            <h5 className={'text-nowrap'}>
                                {pickedData?.psychologist?.user.first_name + ' ' + pickedData?.psychologist?.user.last_name}
                            </h5>
                            {pickedData?.skills && <p>
                                Запрос:{' '}
                                {pickedData?.skills
                                    ?.map((id) =>
                                        skill_categories
                                            .flatMap((cat) => cat.skills)
                                            .find((skill) => skill.id === id)?.name)
                                    .filter((name) => name)
                                    .join(', ')}
                            </p>}
                            {pickedData?.is_psychologist_has_child && <p>Психолог имеет детей</p>}
                        </FCC>
                    </FR>
                </FC>
            </FC>}
            <PaymentSelector
                product={product}
                isLoading={orderCreating}
                onConfirm={(currency, paymentSystem, promocodeId, email) => {
                    createOrder(currency, paymentSystem, promocodeId, email);
                }}
            />
        </div>
    );
};

export default TariffOrder;
